/* eslint-disable sort-keys */
import { useCallback, useMemo } from 'react';

import dayjs from 'dayjs';

import { createLocale } from 'locales';

import type { RowType, TableField } from 'components-new/table/table';

import { useIntegrationSettingsContext } from 'state/context/organization/OrganisationSettings/IntegrationSettingsContext';

import type { GenericImportedJobType } from './use-organisation-settings-integration-select';

const localize = createLocale('organisationIntegrationSettingsLocales');

export interface UseOrganisationSettingsIntegrationSelectTableHandler {
  selectedJobIds: (string | null | undefined)[];
  setSelectedJobIds: React.Dispatch<
    React.SetStateAction<(string | null | undefined)[]>
  >;
  totalPages: number;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  currentPageSize: number;
  setCurrentPageSize: React.Dispatch<React.SetStateAction<number>>;
  fields: TableField[];
  data: RowType[];
  onNumberOfItemsPerPageChange: (n: number) => void;
  onSelectedItemsChange: (
    newSelectedJobs: Array<Record<string, unknown>>,
  ) => void;
  selectedSdbJobs: GenericImportedJobType[];
}

const useOrganisationSettingsIntegrationSelectTable =
  (): UseOrganisationSettingsIntegrationSelectTableHandler => {
    const {
      fetch: { fetchedJobs },
      create: { createdJobIds },
      table: {
        currentPageSize,
        page,
        selectedJobIds,
        setCurrentPageSize,
        setPage,
        setSelectedJobIds,
      },
    } = useIntegrationSettingsContext();

    const totalPages = useMemo(() => {
      return Math.ceil(fetchedJobs.length / currentPageSize);
    }, [currentPageSize, fetchedJobs.length]);

    const fields: TableField[] = [
      {
        name: 'date',
        sortable: false,
        title: localize('date'),
        dataClassName: 'min-w-[100px]',
      },
      { name: 'title', sortable: false, title: localize('title') },
      { name: 'function', sortable: false, title: localize('function') },
      { name: 'start', sortable: false, title: localize('start') },
      { name: 'end', sortable: false, title: localize('end') },
      { name: 'pause', sortable: false, title: localize('break') },
      { name: 'location', sortable: false, title: localize('location') },
    ];

    const data: RowType[] = useMemo(() => {
      const sortedData = fetchedJobs
        .map(
          (job) =>
            ({
              id: job.sdb_job_id,
              date: dayjs(job.date).format('MMM D, YYYY'),
              title: job.title,
              function: job.function,
              start: dayjs(job.start).format('HH:mm'),
              end: dayjs(job.end).format('HH:mm'),
              pause: job.pause,
              location: job.address || '',
            } as GenericImportedJobType),
        )
        .filter((job) => !createdJobIds.includes(job.id))
        .sort((a, b) => (dayjs(a.date).isAfter(dayjs(b.date)) ? 1 : -1));

      const start = (page - 1) * currentPageSize;
      const end = start + currentPageSize;
      return sortedData.slice(start, end);
    }, [createdJobIds, currentPageSize, fetchedJobs, page]);

    const selectedSdbJobs = useMemo(
      () =>
        (data as GenericImportedJobType[]).filter((job) =>
          selectedJobIds?.includes(job.id),
        ),
      [data, selectedJobIds],
    );

    const onNumberOfItemsPerPageChange = useCallback(
      (n: number) => {
        setPage(1);
        setCurrentPageSize(n);
      },
      [setCurrentPageSize, setPage],
    );

    const onSelectedItemsChange = useCallback(
      (newSelectedJobs: Array<Record<string, unknown>>) => {
        const tableSelectedJobsIds = newSelectedJobs.map(
          (value: Record<string, unknown>) => {
            const job = value as unknown as GenericImportedJobType;
            return job.id;
          },
        );
        setSelectedJobIds([...tableSelectedJobsIds]);
      },
      [setSelectedJobIds],
    );

    return {
      selectedJobIds,
      setSelectedJobIds,
      totalPages,
      page,
      setPage,
      currentPageSize,
      setCurrentPageSize,
      fields,
      data,
      onNumberOfItemsPerPageChange,
      onSelectedItemsChange,
      selectedSdbJobs,
    };
  };

export default useOrganisationSettingsIntegrationSelectTable;
